// src/pages/LandingPage.tsx

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { validateInstitutionNumber } from '../services/firebase';
import { useTranslation } from '../i18n';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0369a1 0%, #075985 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const LandingCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 60px 40px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 480px;
  text-align: center;
  position: relative;
`;

const Header = styled.div`
  margin-bottom: 40px;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const LogoText = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 16px;
  margin: 0 0 8px 0;
  line-height: 1.5;
`;

const Description = styled.p`
  color: #9ca3af;
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
`;

const FormSection = styled.div`
  margin-bottom: 32px;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 12px;
  font-weight: 600;
  color: #374151;
  font-size: 16px;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 20px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  transition: all 0.2s ease;
  text-align: center;
  font-weight: 600;
  letter-spacing: 0.5px;
  
  &:focus {
    border-color: #0369a1;
    box-shadow: 0 0 0 3px rgba(3, 105, 161, 0.1);
    outline: none;
  }
  
  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
    letter-spacing: normal;
    text-transform: none;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  background: linear-gradient(135deg, #0369a1 0%, #075985 100%);
  color: white;
  
  &:hover { 
    transform: translateY(-2px); 
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); 
  }
  
  &:active { 
    transform: translateY(0); 
  }
  
  &:disabled { 
    opacity: 0.5; 
    cursor: not-allowed; 
    transform: none !important;
    box-shadow: none !important;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 14px;
  margin-top: 16px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
`;

const FeatureList = styled.div`
  margin-top: 40px;
  padding-top: 32px;
  border-top: 1px solid #e5e7eb;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
  color: #6b7280;
  font-size: 14px;
`;

const FeatureIcon = styled.span`
  font-size: 16px;
`;

const HiddenLink = styled.div`
  position: fixed;
  bottom: 10px;
  right: 10px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  user-select: none;
  
  &:hover {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [institutionNumber, setInstitutionNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!institutionNumber.trim()) {
      setError(t('landing.errors.instNumberRequired'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const trimmedNumber = institutionNumber.trim();
      
      const isValid = await validateInstitutionNumber(trimmedNumber);
      
      if (!isValid) {
        setError(t('landing.errors.invalidInstNumber'));
        return;
      }
      
      sessionStorage.setItem('institutionNumber', trimmedNumber);
      
      navigate('/main');
      
    } catch (error) {
      console.error('기관 확인 오류:', error);
      setError(t('landing.errors.validationError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleHiddenClick = () => {
    navigate('/institution-management');
  };

  return (
    <Container>
      <LandingCard>
        <Header>
          <Logo>
            <img src="/osler_logo.png" alt="Osler" style={{ height: '40px' }} />
            <LogoText>{t('landing.logo')}</LogoText>
          </Logo>
          <Subtitle>{t('landing.subtitle')}</Subtitle>
          <Description>{t('landing.description')}</Description>
        </Header>

        <form onSubmit={handleSubmit}>
          <FormSection>
            <FormGroup>
              <Label htmlFor="institutionNumber">{t('landing.instNumber')}</Label>
              <Input
                id="institutionNumber"
                type="text"
                value={institutionNumber}
                onChange={(e) => setInstitutionNumber(e.target.value)}
                placeholder={t('landing.instPlaceholder')}
                disabled={isLoading}
                maxLength={20}
              />
            </FormGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <Button type="submit" disabled={isLoading || !institutionNumber.trim()}>
              {isLoading ? t('common.loading') : t('landing.enter')}
            </Button>
            <Button 
              type="button" 
              onClick={() => navigate('/patient')}
              style={{ 
                marginTop: '16px',
                background: 'linear-gradient(135deg, #15803d 0%, #166534 100%)'
              }}
            >
              {t('landing.patientPageLink')}
            </Button>
          </FormSection>
        </form>

      </LandingCard>

      <HiddenLink onClick={handleHiddenClick}>
        &nbsp;
      </HiddenLink>
    </Container>
  );
};

export default LandingPage;